import React, { useEffect, useRef, useState } from 'react';
import { studentPreviewHref } from '../../../services/studentPreview';
import { useParams, useNavigate } from 'react-router-dom';
import { lessonPlansApi, sectionsApi, hooksV2Api } from '../../../services/apiV2';
import { Hook, LessonPlan, Section } from '../../../types/v2';
import TeacherNav from '../../../components/TeacherNav';
import { AppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';

interface StudentRow {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  grade_level: string;
  student_id: string;
  group_name?: string;
  enrolled_at: string;
}

interface HookAssignment {
  id: number;
  hook: number;
  section: number;
}

export const V2SectionManage: React.FC = () => {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_sections');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [sectionName, setSectionName] = useState('');
  const [sections, setSections] = useState<Section[]>([]);
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [hooks, setHooks] = useState<Hook[]>([]);
  const [lessonPlans, setLessonPlans] = useState<LessonPlan[]>([]);
  const [assignedHookIds, setAssignedHookIds] = useState<number[]>([]);
  const [sectionJoinCode, setSectionJoinCode] = useState('');
  const [autoMakeupForAbsences, setAutoMakeupForAbsences] = useState(true);
  const [autoMakeupSaving, setAutoMakeupSaving] = useState(false);

  const [addEmail, setAddEmail] = useState('');
  const [addFirst, setAddFirst] = useState('');
  const [addLast, setAddLast] = useState('');

  const [joinCodeLoading, setJoinCodeLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [copyingLessonPlan, setCopyingLessonPlan] = useState(false);
  const [reusingPlans, setReusingPlans] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [copySourceSectionId, setCopySourceSectionId] = useState('');
  const [copySourcePlanId, setCopySourcePlanId] = useState('');
  const [reuseSourceSectionId, setReuseSourceSectionId] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [groupNameDraft, setGroupNameDraft] = useState('');
  const [assigningGroup, setAssigningGroup] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    if (!sectionId) return;
    const id = Number(sectionId);
    const [studentsRes, hooksRes, sectionsRes, lessonPlansRes] = await Promise.all([
      sectionsApi.students(id),
      hooksV2Api.list(),
      sectionsApi.list(),
      lessonPlansApi.list(),
    ]);
    setStudents(studentsRes.data?.students || []);
    const hooksData = Array.isArray(hooksRes.data) ? hooksRes.data : hooksRes.data?.results || [];
    setHooks(hooksData);
    const lessonPlansData = Array.isArray(lessonPlansRes.data) ? lessonPlansRes.data : lessonPlansRes.data?.results || [];
    setLessonPlans(lessonPlansData.filter((plan: LessonPlan) => !plan.is_archived));

    const sectionsData = Array.isArray(sectionsRes.data) ? sectionsRes.data : sectionsRes.data?.results || [];
    setSections(sectionsData);
    const section = sectionsData.find((s: any) => s.id === id);
    setSectionName(section?.name || `Section ${sectionId}`);
    setSectionJoinCode(section?.join_code || '');
    setAutoMakeupForAbsences(section?.auto_makeup_for_absent_live_lessons !== false);

    // Determine which hooks are assigned to this section
    const assigned: number[] = [];
    for (const hook of hooksData) {
      if (hook.assignments) {
        const match = (hook.assignments as any[]).find((a: any) => a.section === id);
        if (match) assigned.push(hook.id);
      }
    }
    setAssignedHookIds(assigned);
  };

  useEffect(() => {
    load();
  }, [sectionId]);

  useEffect(() => {
    setSelectedStudentIds([]);
  }, [sectionId, students.length]);

  const handleJoinCode = async (regenerate = false) => {
    if (!sectionId) return;
    setJoinCodeLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await sectionsApi.joinCode(Number(sectionId), regenerate);
      setSectionJoinCode(response.data?.join_code || '');
      setSuccess(regenerate ? 'Class code regenerated.' : 'Class code created.');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to update class code');
    } finally {
      setJoinCodeLoading(false);
    }
  };

  const handleAutoMakeupPreference = async (enabled: boolean) => {
    if (!sectionId) return;
    const previousValue = autoMakeupForAbsences;
    setAutoMakeupForAbsences(enabled);
    setAutoMakeupSaving(true);
    setError('');
    setSuccess('');
    try {
      await sectionsApi.update(Number(sectionId), {
        auto_makeup_for_absent_live_lessons: enabled,
      });
      setSections((current) => current.map((section) => (
        section.id === Number(sectionId)
          ? { ...section, auto_makeup_for_absent_live_lessons: enabled }
          : section
      )));
      setSuccess(enabled
        ? 'Automatic live-lesson make-up preparation enabled.'
        : 'Automatic live-lesson make-up preparation disabled.');
    } catch (e: any) {
      setAutoMakeupForAbsences(previousValue);
      setError(e?.response?.data?.error || 'Failed to update live-lesson make-up preference');
    } finally {
      setAutoMakeupSaving(false);
    }
  };

  const handleAddStudent = async () => {
    if (!sectionId || !addEmail.trim()) return;
    setError('');
    setSuccess('');
    try {
      // Use import with a single-row approach: create user via enroll endpoint
      // The enroll endpoint needs user IDs, so we use the import endpoint with a minimal payload
      const blob = new Blob(
        [`first_name,last_name,email\n${addFirst || 'Student'},${addLast || ''},${addEmail.trim()}`],
        { type: 'text/csv' },
      );
      const file = new File([blob], 'add.csv', { type: 'text/csv' });
      const response = await sectionsApi.importStudents(Number(sectionId), file);
      const results = response.data?.results;
      if (results?.errors?.length) {
        setError(results.errors.join('; '));
      } else {
        setSuccess(`Student added.`);
        setAddEmail('');
        setAddFirst('');
        setAddLast('');
      }
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to add student');
    }
  };

  const handleWithdraw = async (studentId: number) => {
    if (!sectionId) return;
    setError('');
    setSuccess('');
    try {
      await sectionsApi.withdraw(Number(sectionId), [studentId]);
      setSuccess('Student removed.');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to remove student');
    }
  };

  const toggleStudentSelection = (studentId: number) => {
    setSelectedStudentIds((prev) => (
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    ));
  };

  const handleAssignGroup = async (groupName: string) => {
    if (!sectionId || selectedStudentIds.length === 0) return;
    setAssigningGroup(true);
    setError('');
    setSuccess('');
    try {
      await sectionsApi.assignGroups(Number(sectionId), selectedStudentIds, groupName.trim());
      setSuccess(groupName.trim() ? `Assigned subgroup "${groupName.trim()}".` : 'Cleared subgroup.');
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to update subgroup');
    } finally {
      setAssigningGroup(false);
    }
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !sectionId) return;
    setImporting(true);
    setImportResult(null);
    setError('');
    setSuccess('');
    try {
      const response = await sectionsApi.importStudents(Number(sectionId), file);
      setImportResult(response.data?.results || null);
      setSuccess(`Imported ${response.data?.results?.enrolled || 0} students.`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAssignHook = async (hookId: number) => {
    if (!sectionId) return;
    setError('');
    setSuccess('');
    try {
      await hooksV2Api.assign(hookId, Number(sectionId));
      setAssignedHookIds((prev) => [...prev, hookId]);
      setSuccess('Hook assigned to section.');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to assign hook');
    }
  };

  const handleReusePlans = async () => {
    if (!sectionId || !reuseSourceSectionId) return;
    setReusingPlans(true);
    setError('');
    setSuccess('');
    try {
      const response = await sectionsApi.reusePlans(Number(sectionId), Number(reuseSourceSectionId));
      const sourceSectionName = response.data?.source_section_name || 'the selected section';
      const availableLessonPlans = Number(response.data?.available_lesson_plans || 0);
      const createdAssignments = Number(response.data?.hook_assignments_created || 0);
      const reactivatedAssignments = Number(response.data?.hook_assignments_reactivated || 0);
      const changedAssignments = createdAssignments + reactivatedAssignments;

      if (changedAssignments === 0) {
        setSuccess(`No new lesson plans were added from ${sourceSectionName}.`);
      } else if (availableLessonPlans > 0) {
        setSuccess(
          `Reused ${availableLessonPlans} lesson plan${availableLessonPlans === 1 ? '' : 's'} from ${sourceSectionName}.`,
        );
      } else {
        setSuccess(`Copied hook assignments from ${sourceSectionName}.`);
      }
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to reuse lesson plans');
    } finally {
      setReusingPlans(false);
    }
  };

  const plansForSection = (sourceSectionId: number): LessonPlan[] => lessonPlans.filter((plan) => {
    const planHook = hooks.find((hook) => hook.id === plan.hook);
    if (!planHook || planHook.is_archived) return false;
    return (planHook.assignments || []).some(
      (assignment) => assignment.section === sourceSectionId && assignment.is_active !== false,
    );
  });

  const handleCopyLessonPlan = async () => {
    if (!sectionId || !copySourceSectionId || !copySourcePlanId) return;
    setCopyingLessonPlan(true);
    setError('');
    setSuccess('');
    try {
      const response = await sectionsApi.copyLessonPlan(
        Number(sectionId),
        Number(copySourceSectionId),
        Number(copySourcePlanId),
      );
      const planTitle = response.data?.plan_title || 'Copied lesson plan';
      const hookTitle = response.data?.hook_title || 'copied hook';
      setSuccess(`${planTitle} was copied into ${hookTitle}. It is now available in Plan and Teach.`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to copy lesson plan');
    } finally {
      setCopyingLessonPlan(false);
    }
  };

  const handleCopySourceSectionChange = (value: string) => {
    setCopySourceSectionId(value);
    setCopySourcePlanId('');
  };

  const availableSourceSections = sections.filter((section) => section.id !== Number(sectionId));
  const availableCopySourcePlans = copySourceSectionId ? plansForSection(Number(copySourceSectionId)) : [];

  const pageContent = (
    <div className={redesignEnabled ? 'mx-auto max-w-5xl' : 'max-w-5xl mx-auto p-6'}>
      <div className="flex items-center justify-between">
        <div>
          <button className="text-sm text-indigo-600" onClick={() => navigate('/v2/teacher')}>
            ← Back to Sections
          </button>
          <h1 className="text-2xl font-semibold text-gray-900 mt-1">{sectionName}</h1>
          <p className="text-sm text-gray-500">Manage students and hook assignments</p>
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
      )}
      {success && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">{success}</div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        <div className="space-y-6">
          <section className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-xs uppercase tracking-wide text-gray-400">Students ({students.length})</p>

            {students.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">No students enrolled yet.</p>
            ) : (
              <div className="mt-3 space-y-3">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[220px] flex-1">
                      <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                        Saved Subgroup
                      </label>
                      <input
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                        placeholder="e.g., Python or CompTIA"
                        value={groupNameDraft}
                        onChange={(event) => setGroupNameDraft(event.target.value)}
                      />
                    </div>
                    <button
                      className="px-3 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-60"
                      onClick={() => handleAssignGroup(groupNameDraft)}
                      disabled={assigningGroup || selectedStudentIds.length === 0 || !groupNameDraft.trim()}
                    >
                      {assigningGroup ? 'Saving...' : 'Assign subgroup'}
                    </button>
                    <button
                      className="px-3 py-2 border border-gray-300 text-gray-700 rounded text-sm disabled:opacity-60"
                      onClick={() => handleAssignGroup('')}
                      disabled={assigningGroup || selectedStudentIds.length === 0}
                    >
                      Clear subgroup
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {selectedStudentIds.length} selected
                  </p>
                </div>
                {students.map((student) => (
                  <div key={student.id} className="flex items-center justify-between gap-3 border border-gray-200 rounded px-3 py-2">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1"
                        aria-label={`Select ${student.first_name} ${student.last_name}`.trim()}
                        checked={selectedStudentIds.includes(student.id)}
                        onChange={() => toggleStudentSelection(student.id)}
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {student.first_name} {student.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{student.email}</p>
                        <p className="mt-1 text-xs text-gray-500">
                          {student.group_name ? `Subgroup: ${student.group_name}` : 'No subgroup assigned'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <a href={studentPreviewHref(Number(sectionId), student.id)} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-700 hover:underline" aria-label={`View as student: ${student.first_name} ${student.last_name} (opens in a new tab)`}>View as student</a>
                      {student.grade_level && (
                        <span className="text-xs text-gray-400">{student.grade_level}</span>
                      )}
                      <button
                        className="text-xs text-red-600 hover:underline"
                        onClick={() => handleWithdraw(student.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-xs uppercase tracking-wide text-gray-400">Add Student</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input
                className="border rounded px-3 py-2 text-sm"
                placeholder="First name"
                value={addFirst}
                onChange={(e) => setAddFirst(e.target.value)}
              />
              <input
                className="border rounded px-3 py-2 text-sm"
                placeholder="Last name"
                value={addLast}
                onChange={(e) => setAddLast(e.target.value)}
              />
              <input
                className="border rounded px-3 py-2 text-sm"
                placeholder="Email (required)"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
              />
            </div>
            <button
              className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-60"
              onClick={handleAddStudent}
              disabled={!addEmail.trim()}
            >
              Add Student
            </button>
          </section>

          <section className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-xs uppercase tracking-wide text-gray-400">Import Students</p>
            <p className="mt-2 text-sm text-gray-500">
              Upload a CSV or Excel file with columns: <strong>first_name</strong>, <strong>last_name</strong>, <strong>email</strong>.
              Optional: <strong>grade_level</strong>, <strong>student_id</strong>.
            </p>
            <div className="mt-3 flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="text-sm"
                onChange={handleFileImport}
                disabled={importing}
              />
              {importing && <span className="text-sm text-gray-500">Importing…</span>}
            </div>
            {importResult && (
              <div className="mt-3 text-sm text-gray-700 bg-gray-50 rounded p-3">
                <p>Processed: {importResult.total} · Created: {importResult.created} · Enrolled: {importResult.enrolled}</p>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2 text-xs text-red-600">
                    {importResult.errors.map((err: string, i: number) => (
                      <p key={i}>{err}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>

        <aside className="space-y-6">
          <section className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-xs uppercase tracking-wide text-gray-400">Live Lesson Absences</p>
            <label className="mt-3 flex items-start gap-3 text-sm text-gray-800">
              <input
                type="checkbox"
                className="mt-0.5 rounded"
                checked={autoMakeupForAbsences}
                onChange={(event) => void handleAutoMakeupPreference(event.target.checked)}
                disabled={autoMakeupSaving}
              />
              <span>
                <span className="font-medium">Automatically prepare make-up work</span>
                <span className="mt-1 block text-xs leading-5 text-gray-500">
                  At End Run, students who did not enter are preselected as absent with make-up enabled. You still confirm the list before assignment.
                </span>
              </span>
            </label>
            {autoMakeupSaving && <p className="mt-2 text-xs text-gray-500">Saving…</p>}
          </section>

          <section className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-xs uppercase tracking-wide text-gray-400">Copy One Lesson Plan</p>
            <p className="mt-2 text-sm text-gray-500">
              Create a real lesson plan copy for this section. The copied hook and lesson will appear in Plan and Teach.
            </p>
            {availableSourceSections.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">Create another section first to copy from it.</p>
            ) : (
              <>
                <label htmlFor="copy-source-section" className="mt-4 block text-sm font-medium text-gray-700">
                  Source Section for Copy
                </label>
                <select
                  id="copy-source-section"
                  className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={copySourceSectionId}
                  onChange={(event) => handleCopySourceSectionChange(event.target.value)}
                  disabled={copyingLessonPlan}
                >
                  <option value="">Select a section</option>
                  {availableSourceSections.map((section) => (
                    <option key={`copy-source-section-${section.id}`} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </select>
                <label htmlFor="copy-source-plan" className="mt-4 block text-sm font-medium text-gray-700">
                  Lesson Plan
                </label>
                <select
                  id="copy-source-plan"
                  className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={copySourcePlanId}
                  onChange={(event) => setCopySourcePlanId(event.target.value)}
                  disabled={!copySourceSectionId || copyingLessonPlan}
                >
                  <option value="">{copySourceSectionId ? 'Select a lesson plan' : 'Choose a section first'}</option>
                  {availableCopySourcePlans.map((plan) => (
                    <option key={`copy-source-plan-${plan.id}`} value={plan.id}>
                      {plan.title}
                    </option>
                  ))}
                </select>
                <button
                  className="mt-3 w-full px-4 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-60"
                  onClick={handleCopyLessonPlan}
                  disabled={!copySourceSectionId || !copySourcePlanId || copyingLessonPlan}
                >
                  {copyingLessonPlan ? 'Copying...' : 'Copy Lesson Plan'}
                </button>
              </>
            )}
          </section>

          <section className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-xs uppercase tracking-wide text-gray-400">Reuse All Assigned Plans</p>
            <p className="mt-2 text-sm text-gray-500">
              Copy active hook assignments from another section so its lesson plans are available here in Teach without cloning them into Plan.
            </p>
            {availableSourceSections.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">Create another section first to reuse its plans.</p>
            ) : (
              <>
                <label htmlFor="reuse-source-section" className="mt-4 block text-sm font-medium text-gray-700">
                  Source Section for Reuse
                </label>
                <select
                  id="reuse-source-section"
                  className="mt-2 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={reuseSourceSectionId}
                  onChange={(event) => setReuseSourceSectionId(event.target.value)}
                  disabled={reusingPlans}
                >
                  <option value="">Select a section</option>
                  {availableSourceSections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.name}
                    </option>
                  ))}
                </select>
                <button
                  className="mt-3 w-full px-4 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-60"
                  onClick={handleReusePlans}
                  disabled={!reuseSourceSectionId || reusingPlans}
                >
                  {reusingPlans ? 'Reusing...' : 'Reuse Lesson Plans'}
                </button>
              </>
            )}
          </section>

          <section className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-xs uppercase text-gray-400">Class Code</p>
            <p className="mt-2 text-sm text-gray-500">
              Students use this code from their dashboard to join this section.
            </p>
            {sectionJoinCode ? (
              <div className="mt-4 space-y-3">
                <div className="rounded border border-gray-200 bg-gray-50 px-4 py-3 text-center font-mono text-2xl font-semibold text-gray-900">
                  {sectionJoinCode}
                </div>
                <button
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm disabled:opacity-60"
                  onClick={() => handleJoinCode(true)}
                  disabled={joinCodeLoading}
                >
                  {joinCodeLoading ? 'Updating...' : 'Regenerate Code'}
                </button>
              </div>
            ) : (
              <button
                className="mt-4 w-full px-4 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-60"
                onClick={() => handleJoinCode(false)}
                disabled={joinCodeLoading}
              >
                {joinCodeLoading ? 'Creating...' : 'Create Class Code'}
              </button>
            )}
            <p className="mt-3 text-xs text-gray-500">
              Only students signed in with an elevate208.org email can join by code.
            </p>
          </section>

          <section className="bg-white rounded-lg border border-gray-200 p-5">
            <p className="text-xs uppercase tracking-wide text-gray-400">Hook Assignments</p>
            {hooks.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">No hooks created yet.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {hooks.map((hook) => {
                  const isAssigned = assignedHookIds.includes(hook.id);
                  return (
                    <div key={hook.id} className="flex items-center justify-between border border-gray-200 rounded px-3 py-2">
                      <p className="text-sm text-gray-900">{hook.title}</p>
                      {isAssigned ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Assigned</span>
                      ) : (
                        <button
                          className="text-xs text-indigo-600 hover:underline"
                          onClick={() => handleAssignHook(hook.id)}
                        >
                          Assign
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );

  return (
    <>
      {!redesignEnabled && <TeacherNav activeTab="students" />}
      {redesignEnabled ? (
        <AppShell role="teacher" navItems={teacherV2NavItems('students')} title="Students">
          {pageContent}
        </AppShell>
      ) : pageContent}
    </>
  );
};
